import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  History,
  Menu,
  X,
  LogOut,
  Settings,
  GraduationCap,
  BookOpen,
  Users,
  Headphones
} from 'lucide-react';
import { useStore } from '../../stores';
import { buildStaticUrl } from '../../config/env';
import { supportApi } from '../../services/supportApi';
import { MessageDialog } from '../common/MessageDialog';
import { SupportChatModal } from '../support/SupportChatModal';

interface MainLayoutProps {
  children: React.ReactNode;
}

/**
 * 主布局组件 - 包含侧边栏导航和主内容区域
 */
export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useStore();

  const isTeacher = user?.role === 'teacher' || user?.role === 'super_admin';
  const isParent = user?.role === 'parent';

  const navigation = [
    { name: '对话', href: '/chat', icon: MessageSquare },
    { name: '我的作业', href: '/assignments', icon: BookOpen },
    { name: '历史记录', href: '/history', icon: History },
    { name: '设置', href: '/settings', icon: Settings },
  ];

  const [contactingSupport, setContactingSupport] = useState(false);
  const [supportConversationId, setSupportConversationId] = useState<string | null>(null);
  const [supportUnread, setSupportUnread] = useState(0);
  const [supportDialog, setSupportDialog] = useState<{
    open: boolean;
    type: 'success' | 'error' | 'info';
    title: string;
    message: string;
  }>({ open: false, type: 'info', title: '', message: '' });

  // 轮询客服未读数（仅登录用户）
  useEffect(() => {
    if (!user) return;
    let mounted = true;
    const refresh = () => {
      supportApi
        .getUnread()
        .then((n) => {
          if (mounted) setSupportUnread(n);
        })
        .catch(() => {});
    };
    refresh();
    const timer = setInterval(refresh, 30000);
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, [user]);

  const handleLogout = () => {
    logout();
  };

  const handleContactSupport = async () => {
    if (contactingSupport) return;
    setContactingSupport(true);
    try {
      const result = await supportApi.openConversation();
      setSidebarOpen(false);
      // 打开实时聊天窗口（新建会话时后端已发送客服通知短信）
      setSupportConversationId(result.conversation.id);
      // 标记已读并清除未读红标
      supportApi.markRead().catch(() => {});
      setSupportUnread(0);
    } catch (error) {
      setSupportDialog({
        open: true,
        type: 'error',
        title: '连接客服失败',
        message: '打开客服会话时出错，请稍后再试。',
      });
    } finally {
      setContactingSupport(false);
    }
  };

  const handleSupportChatClose = () => {
    setSupportConversationId(null);
    // 关闭聊天窗口时标记已读并刷新未读
    supportApi.markRead().catch(() => {});
    setSupportUnread(0);
  };

  return (
    <div className="flex min-h-screen">
      {/* 移动端侧边栏遮罩 */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* 侧边栏 - 桌面端固定，移动端滑动 */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0 lg:z-0 lg:h-screen lg:sticky lg:top-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo 区域 - 固定不滚动 */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">AI</span>
              </div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                数理化助手
              </h1>
            </div>

            {/* 移动端关闭按钮 */}
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* 用户信息 - 固定不滚动 */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <Link
              to="/profile"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 group"
            >
              {user?.avatar ? (
                <img
                  src={buildStaticUrl(user.avatar, true)}
                  alt="用户头像"
                  className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600"
                />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white font-semibold">
                  {user?.nickname?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                  {user?.nickname || '用户'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user?.email || user?.phone || '点击编辑资料'}
                </p>
              </div>
              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          </div>

          {/* 导航菜单 - 可滚动区域 */}
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href ||
                (item.href === '/chat' && location.pathname === '/');

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200
                    ${isActive
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900 dark:text-primary-200'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
                    }
                  `}
                >
                  <item.icon className={`
                    mr-3 h-5 w-5 transition-colors duration-200
                    ${isActive
                      ? 'text-primary-500 dark:text-primary-400'
                      : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                    }
                  `} />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* 底部操作 - 固定不滚动 */}
          <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700 space-y-2 flex-shrink-0">
            {/* 联系我们 - 打开客服会话 */}
            <button
              onClick={handleContactSupport}
              disabled={contactingSupport}
              className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 disabled:opacity-60"
            >
              <Headphones className="mr-3 h-5 w-5" />
              <span className="flex-1 text-left">
                {contactingSupport ? '正在连接客服…' : '联系我们'}
              </span>
              {supportUnread > 0 && (
                <span className="ml-2 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs leading-5 text-center">
                  {supportUnread > 99 ? '99+' : supportUnread}
                </span>
              )}
            </button>

            {/* 教师模式切换 - 仅教师和超级管理员可见 */}
            {isTeacher && (
              <button
                onClick={() => {
                  navigate('/teach/students');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-200"
              >
                <GraduationCap className="mr-3 h-5 w-5" />
                教师模式
              </button>
            )}

            {/* 家长模式入口 - 仅家长可见 */}
            {isParent && (
              <button
                onClick={() => {
                  navigate('/parent/family');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-200"
              >
                <Users className="mr-3 h-5 w-5" />
                家长模式
              </button>
            )}
            
            {/* 退出登录 */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200"
            >
              <LogOut className="mr-3 h-5 w-5" />
              退出登录
            </button>
          </div>
        </div>
      </div>

      {/* 主内容区域 */}
      <div className="flex-1 flex flex-col min-h-screen lg:ml-0">
        {/* 顶部导航栏 - 移动端固定 */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 lg:hidden flex-shrink-0 fixed top-0 left-0 right-0 z-30">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">AI</span>
              </div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                数理化助手
              </h1>
            </div>

            <div className="w-10" /> {/* 占位符保持居中 */}
          </div>
        </header>

        {/* 主内容 - 可以自然扩展 */}
        <main className="flex-1 pt-16 lg:pt-0">
          {children}
        </main>
      </div>

      {/* 客服会话结果提示 */}
      <MessageDialog
        isOpen={supportDialog.open}
        type={supportDialog.type}
        title={supportDialog.title}
        message={supportDialog.message}
        onClose={() => setSupportDialog((prev) => ({ ...prev, open: false }))}
      />

      {/* 客服聊天窗口 */}
      {supportConversationId && (
        <SupportChatModal
          conversationId={supportConversationId}
          onClose={handleSupportChatClose}
        />
      )}
    </div>
  );
};